import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const ReplacementList = () => {
  const [replacements, setReplacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchDebounceRef = useRef(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [acceptModal, setAcceptModal] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [acceptInput, setAcceptInput] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [penaltyAmount, setPenaltyAmount] = useState('');
  const [isPenalty, setIsPenalty] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [proofModal, setProofModal] = useState(false);
  const [proofImages, setProofImages] = useState([]);
  const [proofAudio, setProofAudio] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [selectedReplacement, setSelectedReplacement] = useState(null);
  const [historyModal, setHistoryModal] = useState(false);
  const [historyReplacement, setHistoryReplacement] = useState(null);
  const totalPages = Math.max(1, Math.ceil(totalRecords / recordsPerPage));

  const fetchReplacements = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        draw: 1,
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: keyword,
        date: dateFrom && dateTo ? `${dateFrom} - ${dateTo}` : dateFrom || '',
        type: statusFilter
      };
      
      const response = await apiService.post('replacementV1', params);
      if (response && response.data) {
        setReplacements(response.data);
        setTotalRecords(response.recordsFiltered || response.recordsTotal || 0);
      }
    } catch (error) {
      toast.error('Failed to load replacement list');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, recordsPerPage, keyword, dateFrom, dateTo, statusFilter]);

  useEffect(() => {
    fetchReplacements();
  }, [fetchReplacements]);

  const handleAccept = (id) => {
    setSelectedId(id);
    setAcceptInput('');
    setAcceptModal(true);
  };

  const handleReject = (id, penalty = false) => {
    setSelectedId(id);
    setRejectReason('');
    setPenaltyAmount('');
    setIsPenalty(penalty);
    setRejectModal(true);
  };

  const submitAccept = async () => {
    setSubmitting(true);
    try {
      const response = await apiService.put(`replacementStatusV1/${selectedId}`, {
        type: 'Accepted',
        rejectMessage: acceptInput
      });
      toast.success('Replacement accepted successfully');
      setAcceptModal(false);
      fetchReplacements();
    } catch (error) {
      toast.error('Failed to accept replacement');
    } finally {
      setSubmitting(false);
    }
  };

  const submitReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please enter reject reason');
      return;
    }
    if (isPenalty && (!penaltyAmount || penaltyAmount <= 0)) {
      toast.error('Please enter valid penalty amount');
      return;
    }

    setSubmitting(true);
    try {
      const url = isPenalty ? `replacementPenaltyV1/${selectedId}` : `replacementStatusV1/${selectedId}`;
      const data = {
        type: isPenalty ? 'Penalty' : 'Rejected',
        rejectMessage: rejectReason
      };
      if (isPenalty) {
        data.amount = parseFloat(penaltyAmount);
      }

      const response = await apiService.put(url, data);
      toast.success(response.message || 'Replacement rejected successfully');
      setRejectModal(false);
      fetchReplacements();
    } catch (error) {
      toast.error('Failed to reject replacement');
    } finally {
      setSubmitting(false);
    }
  };

  const viewProof = (images, audio) => {
    setProofImages(images);
    setProofAudio(audio);
    setProofModal(true);
  };

  const openViewModal = (item) => {
    setSelectedReplacement(item);
    setViewModal(true);
  };

  const openHistoryModal = (item) => {
    setHistoryReplacement(item);
    setHistoryModal(true);
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-IN'),
      time: date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return '--';
    const date = new Date(dateString);
    return Number.isNaN(date.getTime()) ? '--' : date.toLocaleDateString('en-IN');
  };

  const formatBool = (value) => (value ? 'Yes' : 'No');

  const getStatusBadge = (status) => {
    const config = {
      Pending: 'bg-yellow-100 text-yellow-700',
      Accepted: 'bg-green-100 text-green-700',
      Rejected: 'bg-red-100 text-red-700',
      Penalty: 'bg-red-100 text-red-700'
    };
    return config[status] || config.Pending;
  };

  const getAiResultBadge = (airesult) => {
    const intent = airesult?.intent || 'LOW';
    const config = {
      HIGH: 'bg-emerald-50 text-emerald-700 border border-emerald-100',
      MEDIUM: 'bg-amber-50 text-amber-700 border border-amber-100',
      LOW: 'bg-slate-100 text-slate-700 border border-slate-200'
    };

    return {
      label: intent,
      color: config[intent] || config.LOW
    };
  };

  const formatAiScore = (airesult) => {
    if (airesult?.finalScore !== undefined && airesult?.finalScore !== null) return airesult.finalScore;
    if (airesult?.score !== undefined && airesult?.score !== null) return airesult.score;
    return '--';
  };

  const mediaUrl = process.env.REACT_APP_MEDIA_URL;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <div className="p-0">
      <nav className="flex flex-wrap items-center text-xs sm:text-sm mb-3">
        <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">Home</Link>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-900 font-medium">Replacement List</span>
      </nav>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 sm:p-6 border-b">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => {
                  const val = e.target.value;
                  setSearchInput(val);
                  clearTimeout(searchDebounceRef.current);
                  searchDebounceRef.current = setTimeout(() => {
                    setKeyword(val);
                    setCurrentPage(1);
                  }, 500);
                }}
                placeholder="Type Name, Phone or Email"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
              placeholder="From date"
              className="w-full sm:w-[150px] px-2 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
              placeholder="To date"
              className="w-full sm:w-[150px] px-2 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="w-full sm:w-full sm:w-[150px] px-2 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">All</option>
              <option value="Pending">Pending</option>
              <option value="Accepted">Accepted</option>
              <option value="Rejected">Rejected</option>
              <option value="Penalty">Penalty</option>
            </select>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{borderColor: '#009ef7'}}></div>
              <span className="ml-2 text-gray-600">Loading...</span>
            </div>
          ) : replacements.length === 0 ? (
            <div className="text-center py-12">
              <i className="bi bi-arrow-repeat text-4xl text-gray-300 mb-2"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No Replacements Found</h3>
              <p className="text-gray-500">No replacements match your criteria.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-gray-500 text-xs uppercase">
                    <th className="px-2 sm:px-3 py-2">#</th>
                    <th className="px-2 sm:px-3 py-2">Vendor</th>
                    <th className="px-2 sm:px-3 py-2 min-w-[160px]">Query Info</th>
                    <th className="px-2 sm:px-3 py-2 min-w-[160px]">Trip Info</th>
                    <th className="px-2 sm:px-3 py-2">Customer</th>
                    <th className="px-2 sm:px-3 py-2 min-w-[240px]">Replacement</th>
                    <th className="px-2 sm:px-3 py-2 min-w-[240px]">Reject Msg</th>
                    <th className="px-2 sm:px-3 py-2 text-center">Status</th>
                    <th className="px-2 sm:px-3 py-2 text-center">%</th>
                    <th className="px-2 sm:px-3 py-2 text-center">Actions</th>
                    <th className="px-2 sm:px-3 py-2 min-w-[220px]">Dates</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {replacements.map((item, index) => {
                    const vendor = item.userId || {};
                    const query = item.queryId || {};
                    const customer = query.userData || {};
                    const replacement = item.replacementRequest || {};
                    const unlockDate = formatDateTime(item.createdAt);
                    const queryDate = query.createdAt ? formatDateTime(query.createdAt) : null;
                    const replacementDate = item.replacementRequestedAt ? formatDateTime(item.replacementRequestedAt) : null;
                    const source = query.leavingFrom?.name || 'N/A';
                    const destination = query.goingTo?.name || 'N/A';
                    const querySource = (query.source || 'other').toString().toLowerCase();
                    const sourceBadge = querySource === 'google' ? 'G' : (querySource === 'meta' ? 'M' : 'O');
                    const aiBadge = getAiResultBadge(query.airesult);
                    
                    return (
                      <tr key={item._id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openViewModal(item)}>
                        <td className="px-2 sm:px-3 py-2">{(currentPage - 1) * recordsPerPage + index + 1}</td>
                        <td className="px-2 sm:px-3 py-2 ">
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-800">{vendor.first_name} {vendor.last_name}</span>
                            <Link to={`/vendorWallet/${vendor._id}`} style={{color: '#009ef7'}} className="text-xs no-underline hover:opacity-80" onClick={(e) => e.stopPropagation()}>{vendor.business_name || '-'}</Link>
                            <span className="text-gray-500 text-xs">{vendor.email}</span>
                            <span className="text-gray-500 text-xs">{vendor.phone}</span>
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 py-2">
                          <div className="min-w-[240px] max-w-[320px] text-xs text-slate-700">
                            <div className="flex items-center justify-between gap-3">
                              <a
                                href={`https://www.travellinglark.com/travel-lead/details/${query.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#009ef7' }}
                                className="no-underline hover:opacity-80 font-semibold text-sm leading-5"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Query #{query.id}
                              </a>
                              <div className="flex items-center gap-2">
                                <span
                                  className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-[#009ef7]/10 px-2 text-[10px] font-semibold text-[#009ef7]"
                                  title={`Source: ${querySource}`}
                                >
                                  {sourceBadge}
                                </span>
                                <span
                                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.04em] ${aiBadge.color}`}
                                  title={`Lead priority: ${aiBadge.label}`}
                                >
                                  {aiBadge.label}
                                </span>
                              </div>
                            </div>

                            <div className="mt-2 rounded-xl border border-slate-200 bg-white px-2.5 py-2">
                              <div className="flex items-start gap-2 text-[11px]">
                                <div className="flex flex-col items-center pt-0.5">
                                  <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                                  <span className="h-4 w-px bg-slate-200"></span>
                                  <span className="h-2 w-2 rounded-full bg-rose-500"></span>
                                </div>
                                <div className="min-w-0 flex-1 space-y-1.5">
                                  <div className="flex items-start gap-1.5">
                                    <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.06em] text-slate-400">From</span>
                                    <span className="break-words font-medium text-slate-700">{source}</span>
                                  </div>
                                  <div className="flex items-start gap-1.5">
                                    <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.06em] text-slate-400">To</span>
                                    <span className="break-words font-medium text-slate-700">{destination}</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px]">
                              <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-600">
                                Score: {formatAiScore(query.airesult)}
                              </span>
                              <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 font-semibold capitalize text-slate-600">
                                Call: {query.callStatus || 'pending'}
                              </span>
                              <span className="inline-flex items-center rounded-full px-2.5 py-1 font-semibold" style={{ backgroundColor: '#e8f4fd', color: '#009ef7' }}>
                                {`\u20B9${item.unlockDetail?.amount || 0}`}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 py-2 min-w-[160px]">
                          <div className="flex flex-col gap-1 text-[11px] text-slate-700">
                            <div><span className="font-semibold text-slate-500">Date:</span> {formatDate(query.travelDate)}</div>
                            <div><span className="font-semibold text-slate-500">Pax:</span> {query.adult ?? '--'}A / {query.child ?? 0}C</div>
                            <div><span className="font-semibold text-slate-500">Duration:</span> {query.duration || '--'}</div>
                            <div><span className="font-semibold text-slate-500">Budget:</span> {query.budget ? `₹${query.budget}` : '--'}</div>
                            <div><span className="font-semibold text-slate-500">Hotel:</span> {query.hotelCategory?.length ? query.hotelCategory.join(', ') : '--'}</div>
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 py-2">
                          <div className="flex flex-col text-xs">
                            <span className="font-semibold text-gray-800">{customer.first_name} {customer.last_name}</span>
                            <span className="text-gray-500">{customer.email} {customer.emailVerified ? <i className="bi bi-check-circle-fill text-green-600"></i> : <i className="bi bi-x-circle-fill text-red-600"></i>}</span>
                            <span className="text-gray-500">{customer.phone} {customer.phoneVerified ? <i className="bi bi-check-circle-fill text-green-600"></i> : <i className="bi bi-x-circle-fill text-red-600"></i>}</span>
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 py-2 min-w-[240px]">
                          <div className="flex flex-col text-xs">
                            {item.proof && item.proof.length > 0 && (
                              <button onClick={(e) => { e.stopPropagation(); viewProof(item.proof, item.audio); }} className="px-2 py-1 mb-1 rounded text-xs" style={{backgroundColor: '#e8f4fd', color: '#009ef7'}}>View Proof</button>
                            )}
                            <span className="text-gray-600">Reason: {replacement.reason || '-'}</span>
                            <span className="text-gray-500">{replacement.description || '-'}</span>
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 py-2 text-xs text-gray-600 min-w-[240px]">
                          <div className="max-w-[240px] whitespace-normal break-words leading-5" title={replacement.rejectMessage || '-'}>
                            {replacement.rejectMessage || '-'}
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 py-2 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadge(replacement.verify || 'Pending')}`}>
                              {replacement.verify || 'Pending'}
                            </span>
                            <button
                              onClick={(e) => { e.stopPropagation(); openHistoryModal(item); }}
                              className="px-2 py-1 rounded text-[11px] font-medium border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100"
                            >
                              History
                            </button>
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 py-2 text-center">
                          <div className="flex flex-col">
                            <span className="font-semibold" style={{color: '#009ef7'}}>{(item.percentage || 0).toFixed(1)}%</span>
                            <span className="text-xs text-gray-500">{item.acceptedReplacements || 0}/{item.totalLeads || 0}</span>
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 py-2">
                          <div className="flex flex-col gap-1">
                            {replacement.verify !== 'Accepted' && (
                              <button onClick={(e) => { e.stopPropagation(); handleAccept(item._id); }} className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Accept</button>
                            )}
                            {replacement.verify !== 'Rejected' && (
                              <button onClick={(e) => { e.stopPropagation(); handleReject(item._id, false); }} className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">Reject</button>
                            )}
                            {replacement.verify !== 'Penalty' && (
                              <button onClick={(e) => { e.stopPropagation(); handleReject(item._id, true); }} className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">Penalty</button>
                            )}
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 py-2 text-xs min-w-[220px]">
                          <div className="flex flex-col gap-1">
                            {queryDate && (
                              <div>
                                <span className="text-gray-500 font-semibold">Posted:</span> {queryDate.date} {queryDate.time}
                              </div>
                            )}
                            <div>
                              <span className="text-gray-500 font-semibold">Unlocked:</span> {unlockDate.date} {unlockDate.time}
                            </div>
                            {replacementDate && (
                              <div>
                                <span className="text-gray-500 font-semibold">Replacement:</span> {replacementDate.date} {replacementDate.time}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && replacements.length > 0 && (
          <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-xs sm:text-sm text-gray-700">Show</span>
              <select value={recordsPerPage} onChange={(e) => { setRecordsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="px-3 py-1 border border-gray-300 rounded-lg text-xs sm:text-sm">
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-xs sm:text-sm text-gray-700">entries</span>
            </div>
            <div className="flex items-center gap-1 flex-wrap justify-center">
              <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100">«</button>
              {[...Array(totalPages)].map((_, i) => {
                const page = i + 1;
                if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                  const isActive = currentPage === page;
                  return (
                    <button key={page} onClick={() => setCurrentPage(page)} className="px-3 py-1 border rounded text-xs sm:text-sm" style={{backgroundColor: isActive ? '#009ef7' : '#fff', borderColor: isActive ? '#009ef7' : '#d1d5db', color: isActive ? '#fff' : '#374151'}}>{page}</button>
                  );
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} className="px-2 text-gray-500">...</span>;
                }
                return null;
              })}
              <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100">»</button>
            </div>
          </div>
        )}
      </div>

      {acceptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-[500px]">
            <h3 className="text-xl font-semibold mb-4">Confirm Accept</h3>
            <textarea value={acceptInput} onChange={(e) => setAcceptInput(e.target.value)} rows="4" placeholder="Enter any warning" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg mb-4"></textarea>
            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button onClick={() => setAcceptModal(false)} disabled={submitting} className="px-2 sm:px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Cancel</button>
              <button onClick={submitAccept} disabled={submitting} className="px-2 sm:px-4 py-2 text-white rounded hover:opacity-90" style={{backgroundColor: '#009ef7'}}>
                {submitting ? 'Accepting...' : 'Accept'}
              </button>
            </div>
          </div>
        </div>
      )}

      {rejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-[500px]">
            <h3 className="text-xl font-semibold mb-4">{isPenalty ? 'Reject with Penalty' : 'Reject Reason'}</h3>
            <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows="4" placeholder="Enter reject reason" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg mb-4"></textarea>
            {isPenalty && (
              <input type="number" value={penaltyAmount} onChange={(e) => setPenaltyAmount(e.target.value)} placeholder="Enter penalty amount" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg mb-4" />
            )}
            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button onClick={() => setRejectModal(false)} disabled={submitting} className="px-2 sm:px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Cancel</button>
              <button onClick={submitReject} disabled={submitting} className="px-2 sm:px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">
                {submitting ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewModal && selectedReplacement && (() => {
        const query = selectedReplacement.queryId || {};
        const vendor = selectedReplacement.userId || {};
        const customer = query.userData || {};
        const replacement = selectedReplacement.replacementRequest || {};
        const queryDate = query.createdAt ? formatDateTime(query.createdAt) : { date: '--', time: '--' };
        const unlockDate = selectedReplacement.createdAt ? formatDateTime(selectedReplacement.createdAt) : { date: '--', time: '--' };
        const replacementDate = selectedReplacement.replacementRequestedAt ? formatDateTime(selectedReplacement.replacementRequestedAt) : null;
        return (
          <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-[2px] p-2 sm:p-5">
            <div className="mx-auto max-w-3xl max-h-[92vh] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl flex flex-col font-sans">
              <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 backdrop-blur px-4 sm:px-6 py-2.5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-slate-900 mt-0.5">Lead Overview</h3>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      <span className="inline-flex items-center rounded-full bg-sky-50 text-sky-700 border border-sky-100 px-2.5 py-1 text-xs font-semibold">
                        <i className="bi bi-hash mr-1"></i>{query.id || '--'}
                      </span>
                      <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 border border-blue-100 px-2.5 py-1 text-xs font-semibold">
                        Source: {query.source || '--'}
                      </span>
                      <span className="inline-flex items-center rounded-full bg-orange-50 text-orange-700 border border-orange-100 px-2.5 py-1 text-xs font-semibold">
                        Unlock Amount: {selectedReplacement.unlockDetail?.amount || 0}
                      </span>
                      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${getAiResultBadge(query.airesult).color}`}>
                        {getAiResultBadge(query.airesult).label}
                      </span>
                      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusBadge(replacement.verify || 'Pending')}`}>
                        {replacement.verify || 'Pending'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-right">
                      <p className="text-xs sm:text-sm text-slate-900 font-semibold">{queryDate.date}</p>
                      <p className="text-[11px] text-slate-500">{queryDate.time}</p>
                    </div>
                    <button
                      onClick={() => setViewModal(false)}
                      className="h-8 w-8 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center justify-center"
                      aria-label="Close"
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  </div>
                </div>
              </div>

              <div className="overflow-y-auto p-4 sm:p-5 bg-gradient-to-b from-slate-50 to-white">
                <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2 rounded-xl border border-slate-200 bg-white p-3">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <p className="text-xs uppercase text-slate-600 font-semibold mb-0">Contact Summary</p>
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">Compact</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-1.5 text-[11px] leading-5">
                        <div className="flex items-start gap-2">
                          <span className="min-w-[92px] font-semibold text-blue-700">Traveler</span>
                          <span className="text-slate-900">{customer.first_name || '--'} {customer.last_name || ''}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="min-w-[92px] font-semibold text-emerald-700">Vendor</span>
                          <span className="text-slate-900">{vendor.business_name || '--'}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="min-w-[92px] font-semibold text-slate-600">Email</span>
                          <span className="text-slate-700 break-all">{customer.email || '--'}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="min-w-[92px] font-semibold text-slate-600">Person</span>
                          <span className="text-slate-700">{vendor.first_name || '--'} {vendor.last_name || ''}</span>
                        </div>
                        <div className="flex items-start gap-2 md:col-span-2">
                          <span className="min-w-[92px] font-semibold text-slate-600">Phone</span>
                          <span className="text-slate-700">{customer.phone || '--'}</span>
                        </div>
                        <div className="flex items-start gap-2 md:col-start-2">
                          <span className="min-w-[92px] font-semibold text-slate-600">Vendor Email</span>
                          <span className="text-slate-700 break-all">{vendor.email || '--'}</span>
                        </div>
                        <div className="flex items-start gap-2 md:col-start-2">
                          <span className="min-w-[92px] font-semibold text-slate-600">Vendor Phone</span>
                          <span className="text-slate-700">{vendor.phone || '--'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-xl border border-orange-100 bg-orange-50/70 p-3.5">
                      <p className="text-xs uppercase text-orange-700 font-semibold mb-2">Trip Route</p>
                      <p className="text-slate-800 text-sm"><span className="font-semibold">Travel Date:</span> {formatDate(query.travelDate)}</p>
                      <p className="text-slate-800 text-sm mt-1"><span className="font-semibold">From:</span> {query.leavingFrom?.name || '--'}</p>
                      <p className="text-slate-800 text-sm mt-1"><span className="font-semibold">To:</span> {query.goingTo?.name || '--'}</p>
                    </div>

                    <div className="rounded-xl border border-sky-100 bg-sky-50/70 p-3.5">
                      <p className="text-xs uppercase text-sky-700 font-semibold mb-2">Lead Timeline</p>
                      <p className="text-slate-800 text-sm"><span className="font-semibold">Query Date:</span> {queryDate.date} {queryDate.time}</p>
                      <p className="text-slate-800 text-sm mt-1"><span className="font-semibold">Unlocked:</span> {unlockDate.date} {unlockDate.time}</p>
                      {replacementDate && <p className="text-slate-800 text-sm mt-1"><span className="font-semibold">Replacement:</span> {replacementDate.date} {replacementDate.time}</p>}
                    </div>

                    <div className="sm:col-span-2 rounded-2xl border border-slate-200 bg-white p-3 sm:p-4">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-[10px] uppercase text-slate-500 font-bold tracking-wider">Other Details</p>
                        <span className="inline-flex items-center px-2 py-1 rounded-full bg-slate-100 text-[11px] text-slate-600 font-semibold">Compact</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-2">
                        {[
                          { label: 'Adults / Children', value: `${query.adult ?? '--'} / ${query.child ?? '--'}` },
                          { label: 'Duration', value: query.duration || '--' },
                          { label: 'Total Budget', value: (query.budget != null && query.adult != null) ? `${query.budget * query.adult} ( ${query.budget || 0} * ${query.adult || 0} )` : '--' },
                          { label: 'Tour Type', value: query.tourType || '--' },
                          { label: 'Preferred Call', value: query.preferredCallTime || '--' },
                          { label: 'Will Book', value: query.willBook || '--' },
                          { label: 'Hotel Category', value: query.hotelCategory?.length ? query.hotelCategory.join(', ') : '--' },
                          { label: 'Contacted Agent', value: query.contactedAgent || '--' },
                          { label: 'Dates Flexible', value: query.datesFlexible || '--' },
                          { label: 'Most Important', value: query.mostImportant || '--' },
                          { label: 'Ticket / Reward', value: `${formatBool(query.haveTicket)} / ${formatBool(query.appliedForReward)}` }
                        ].map((detail) => (
                          <div key={detail.label} className="flex items-center text-[11px] leading-4 text-slate-800 min-h-[22px]">
                            <span className="min-w-[110px] text-slate-600 font-semibold">{detail.label}:</span>
                            <span className="text-slate-900 truncate">{detail.value}</span>
                          </div>
                        ))}
                      </div>
                      {query.description ? (
                        <div className="mt-3 rounded-lg bg-slate-50 p-2">
                          <p className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Description</p>
                          <p className="text-xs leading-5 text-slate-700 min-h-[26px] whitespace-pre-wrap">{query.description}</p>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="mt-4 rounded-xl border border-amber-100 bg-amber-50/70 p-3.5">
                    <p className="text-xs uppercase text-amber-700 font-semibold mb-2">Replacement Details</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm text-slate-700">
                      <p><span className="font-semibold text-slate-900">Status:</span> {replacement.verify || 'Pending'}</p>
                      <p><span className="font-semibold text-slate-900">Percentage:</span> {(selectedReplacement.percentage || 0).toFixed(1)}%</p>
                      <p className="sm:col-span-2"><span className="font-semibold text-slate-900">Reason:</span> {replacement.reason || '--'}</p>
                      <p className="sm:col-span-2"><span className="font-semibold text-slate-900">Description:</span> {replacement.description || '--'}</p>
                      <p className="sm:col-span-2"><span className="font-semibold text-slate-900">Reject Message:</span> {replacement.rejectMessage || '--'}</p>
                    </div>
                  </div>

                  <div className="mt-4 rounded-xl border border-violet-100 bg-violet-50/60 p-3.5">
                    <div className="flex flex-wrap items-center gap-2 mb-2.5">
                      <p className="text-xs uppercase text-violet-700 font-semibold mb-0">Lead Priority</p>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.04em] ${getAiResultBadge(query.airesult).color}`}>
                        {getAiResultBadge(query.airesult).label}
                      </span>
                      <span className="text-xs text-slate-600">Score: {formatAiScore(query.airesult)}</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                      <p className="text-sm text-slate-700"><span className="font-semibold text-slate-900">Urgency:</span> {query.airesult?.urgency || '--'}</p>
                      <p className="text-sm text-slate-700"><span className="font-semibold text-slate-900">Confidence:</span> {query.airesult?.confidence ?? '--'}</p>
                      <p className="text-sm text-slate-700"><span className="font-semibold text-slate-900">Hot Lead:</span> {formatBool(query.airesult?.isHotLead)}</p>
                      <p className="text-sm text-slate-700 sm:col-span-2"><span className="font-semibold text-slate-900">Reason:</span> {query.airesult?.reason || '--'}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-200 bg-white px-4 sm:px-6 py-3 flex justify-end">
                <button
                  onClick={() => setViewModal(false)}
                  className="px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 text-sm font-semibold"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {proofModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-[900px] max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Replacement Proof</h3>
              <button onClick={() => setProofModal(false)} className="text-gray-500 hover:text-gray-700">
                <i className="bi bi-x text-2xl"></i>
              </button>
            </div>
            {proofAudio && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold mb-2">Audio Recording</h4>
                <audio controls className="w-full">
                  <source src={`${mediaUrl}${proofAudio}`} type="audio/mpeg" />
                  Your browser does not support the audio element.
                </audio>
              </div>
            )}
            {proofImages.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2">Images</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {proofImages.map((img, i) => (
                    <a key={i} href={`${mediaUrl}${img}`} target="_blank" rel="noopener noreferrer">
                      <img src={`${mediaUrl}${img}`} alt={`Proof ${i + 1}`} className="w-full h-64 object-cover rounded" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {historyModal && historyReplacement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-[760px] max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-xl font-semibold">Replacement History</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Query #{historyReplacement?.queryId?.id || '--'}
                </p>
              </div>
              <button onClick={() => setHistoryModal(false)} className="text-gray-500 hover:text-gray-700">
                <i className="bi bi-x text-2xl"></i>
              </button>
            </div>

            {historyReplacement?.replacementHistory?.length > 0 ? (
              <div className="space-y-3">
                {historyReplacement.replacementHistory.map((history, index) => {
                  const requestedAt = history?.requestedAt ? formatDateTime(history.requestedAt) : null;
                  const verifiedAt = history?.verifiedAt ? formatDateTime(history.verifiedAt) : null;

                  return (
                    <div key={`${history?.requestedAt || index}-${index}`} className="border border-slate-200 rounded-xl p-4 bg-slate-50">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-700">Request #{index + 1}</span>
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadge(history?.status || 'Pending')}`}>
                            {history?.status || 'Pending'}
                          </span>
                        </div>
                        {requestedAt && (
                          <span className="text-xs text-slate-500">
                            Requested: {requestedAt.date} {requestedAt.time}
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-700">
                        <p><span className="font-semibold text-slate-900">Reason:</span> {history?.reason || '--'}</p>
                        <p><span className="font-semibold text-slate-900">Verified At:</span> {verifiedAt ? `${verifiedAt.date} ${verifiedAt.time}` : '--'}</p>
                        <p className="sm:col-span-2">
                          <span className="font-semibold text-slate-900">Description:</span> {history?.description || '--'}
                        </p>
                        <p className="sm:col-span-2">
                          <span className="font-semibold text-slate-900">Reject Message:</span> {history?.rejectMessage || '--'}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-10 text-gray-500">
                No replacement history found.
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setHistoryModal(false)}
                className="px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 text-sm font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReplacementList;




